import React, { useState, useEffect, lazy, Suspense, createContext, useContext, useCallback, useMemo, useRef } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { doc, onSnapshot, getDoc, getDocs, collection } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth, handleFirestoreError, OperationType } from './firebase';
import { StoreConfig, Product } from './types';
import { CATEGORIES } from './constants';
import PWAManager from './components/PWAManager';
import PWAInstallPrompt from './components/Home/PWAInstallPrompt';
import OfflineStatusNotifier from './components/Home/OfflineStatusNotifier';
import PWAUpdateNotifier from './components/Home/PWAUpdateNotifier';
import FontFaceObserver from 'fontfaceobserver';
import WelcomeToast from './components/Home/WelcomeToast';

import Home from './components/Home/Home';

// Lazy load secondary views and heavy admin panels for premium storefront startup performance
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const Login = lazy(() => import('./components/Admin/Login'));
const About = lazy(() => import('./components/Home/About'));
const InvoicePage = lazy(() => import('./components/Home/InvoicePage'));

// Minimal, fast-rendering loading indicator
const LoadingFallback = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-[#FDFCF8] p-4 text-center">
    <div className="w-10 h-10 border-4 border-[#2A5431]/20 border-t-[#2A5431] rounded-full animate-spin mb-4" />
    <p className="text-[10px] uppercase tracking-widest text-[#6B7280] font-mono">Loading...</p>
  </div>
);

// Context for global state
interface AppContextType {
  isAdmin: boolean;
  config: StoreConfig;
  handleLoginSuccess: () => void;
  handleLogout: () => void;
}

export const AppContext = createContext<AppContextType | null>(null);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within AppContext.Provider');
  return context;
};

// Root Layout Component to manage state and global UI
function RootLayout() {
  const [isAdmin, setIsAdmin] = useState<boolean>(() => {
    try {
      return localStorage.getItem('adm_auth_session') === 'true';
    } catch {
      return false;
    }
  });
  const [config, setConfig] = useState<StoreConfig>(() => {
    try {
      const saved = localStorage.getItem('rk_config_cache');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [toastData, setToastData] = useState<{ show: boolean; userName: string; userPhoto: string | null }>({
    show: false,
    userName: '',
    userPhoto: null
  });

  const isInitialAuth = useRef(true);

  useEffect(() => {
    // Detect when Inter (Google Fonts) has loaded, preventing FOUT
    const fontObserver = new FontFaceObserver('Inter');
    fontObserver.load(null, 5000)
      .then(() => {
        document.documentElement.classList.add('fonts-loaded');
      })
      .catch((err) => {
        console.warn('[FontObserver] Inter font load timeout or error. Graceful fallback to default system fonts.', err);
        document.documentElement.classList.add('fonts-loaded');
      });

    // Sync with Firebase Auth state
    const unsubAuth = onAuthStateChanged(auth, async (currentUser) => {
      const adminEmails = ['tarzanmaurya1234@gmail.com', 'rajkiranastore0123@gmail.com'];
      if (currentUser) {
        try {
          let isUserAdmin = false;
          if (currentUser.email && adminEmails.includes(currentUser.email.toLowerCase())) {
            isUserAdmin = true;
          } else if (currentUser.email) {
            try {
              const idToken = await currentUser.getIdToken();
              const res = await fetch('/api/check-admin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken })
              });
              const data = await res.json();
              if (data && data.isAdmin) {
                isUserAdmin = true;
              }
            } catch (fetchErr) {
              console.warn('[AuthClaimCheck] Backend API check note:', fetchErr);
            }
          }

          setIsAdmin(isUserAdmin);
          if (isUserAdmin) {
            localStorage.setItem('adm_auth_session', 'true');
          } else {
            localStorage.removeItem('adm_auth_session');
          }
        } catch (err) {
          console.error('[AuthClaimCheck] Error verifying admin status:', err);
          const fallbackAdmin = !!(currentUser.email && adminEmails.includes(currentUser.email.toLowerCase()));
          setIsAdmin(fallbackAdmin);
          if (fallbackAdmin) {
            localStorage.setItem('adm_auth_session', 'true');
          } else {
            localStorage.removeItem('adm_auth_session');
          }
        }
      } else {
        setIsAdmin(false);
        localStorage.removeItem('adm_auth_session');
      }

      // Welcome Toast Trigger on successful explicit Google Login
      if (currentUser) {
        if (!isInitialAuth.current) {
          setToastData({
            show: true,
            userName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Customer',
            userPhoto: currentUser.photoURL
          });
        }
      }
      isInitialAuth.current = false;
    });

    // Aggressive 'stale-while-revalidate' strategy for Firestore config
    // 1. Immediately hydrate from memory/localStorage
    let globalData: StoreConfig = config;
    let banner1Data: any = config.banner1 || null;
    let banner2Data: any = config.banner2 || null;
    let inlineAdsData: any = config.inlineAds ? { ads: config.inlineAds } : null;

    const revalidateConfig = () => {
      const mergedConfig = {
        ...globalData,
        categoryImages: globalData.categoryImages || {},
        banner1: banner1Data || undefined,
        banner2: banner2Data || undefined,
        inlineAds: inlineAdsData?.ads || inlineAdsData?.inlineAds || undefined,
      };
      setConfig(mergedConfig);
      
      // Update the cache aggressively
      try {
        localStorage.setItem('rk_config_cache', JSON.stringify(mergedConfig));
      } catch (err) {
        console.error('Failed to write config cache:', err);
      }
    };

    // 2. Fire immediate revalidate to make sure state matches initial config
    revalidateConfig();

    // 3. Preload strategy: execute getDocs/getDoc on critical collections (products/categories/config)
    // as an immediate fallback so the UI populates instantly on initial load without waiting for the live listener handshake
    const preloadCriticalCollections = async () => {
      try {
        const [productsSnap, configSnap, banner1Snap, banner2Snap, inlineAdsSnap] = await Promise.allSettled([
          getDocs(collection(db, 'products')),
          getDoc(doc(db, 'config', 'global')),
          getDoc(doc(db, 'config', 'banner1')),
          getDoc(doc(db, 'config', 'banner2')),
          getDoc(doc(db, 'config', 'inlineAds'))
        ]);

        // Preload products collection
        if (productsSnap.status === 'fulfilled' && !productsSnap.value.empty) {
          const rawProds = productsSnap.value.docs.map(d => ({ id: d.id, ...d.data() } as Product));
          const sortedProds = rawProds.sort((a, b) => {
            const orderA = a.order || 0;
            const orderB = b.order || 0;
            if (orderA > 0 && orderB > 0) {
              if (orderA !== orderB) return orderA - orderB;
            } else if (orderA > 0) {
              return -1;
            } else if (orderB > 0) {
              return 1;
            }
            const getCollectionPriority = (p: Product) => {
              if (p.isSpecial || p.isOffer) return 0;
              if (p.isNewArrival) return 1;
              if (p.isBestSeller) return 2;
              if (p.isTrending) return 3;
              return 4;
            };
            const colPriA = getCollectionPriority(a);
            const colPriB = getCollectionPriority(b);
            if (colPriA !== colPriB) return colPriA - colPriB;
            const catPriA = CATEGORIES.indexOf(a.category as any);
            const catPriB = CATEGORIES.indexOf(b.category as any);
            const cleanCatA = catPriA === -1 ? 999 : catPriA;
            const cleanCatB = catPriB === -1 ? 999 : catPriB;
            if (cleanCatA !== cleanCatB) return cleanCatA - cleanCatB;
            return (b.createdAt || 0) - (a.createdAt || 0);
          });

          try {
            localStorage.setItem('rk_products_cache', JSON.stringify(sortedProds));
          } catch (e) {
            console.error('Failed to cache preloaded products:', e);
          }

          window.dispatchEvent(new CustomEvent('rk_products_preloaded', { detail: sortedProds }));
        }

        // Preload config and category settings
        const globalObj = configSnap.status === 'fulfilled' && configSnap.value.exists() ? configSnap.value.data() as StoreConfig : null;
        const b1Obj = banner1Snap.status === 'fulfilled' && banner1Snap.value.exists() ? banner1Snap.value.data() : null;
        const b2Obj = banner2Snap.status === 'fulfilled' && banner2Snap.value.exists() ? banner2Snap.value.data() : null;
        const adsObj = inlineAdsSnap.status === 'fulfilled' && inlineAdsSnap.value.exists() ? inlineAdsSnap.value.data() : null;

        if (globalObj) globalData = globalObj;
        if (b1Obj) banner1Data = b1Obj;
        if (b2Obj) banner2Data = b2Obj;
        if (adsObj) inlineAdsData = adsObj;
        if (globalObj || b1Obj || b2Obj || adsObj) {
          revalidateConfig();
        }
      } catch (err) {
        console.warn('[RootLayout Preload] Initial getDocs fallback completed:', err);
      }
    };

    preloadCriticalCollections();

    // 4. Connect live onSnapshot listeners in the background
    const unsubConfig = onSnapshot(doc(db, 'config', 'global'), { includeMetadataChanges: true }, (snap) => {
      if (snap.exists()) {
        globalData = snap.data() as StoreConfig;
      } else {
        globalData = {};
      }
      revalidateConfig();
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'config/global');
    });

    const unsubBanner1 = onSnapshot(doc(db, 'config', 'banner1'), { includeMetadataChanges: true }, (snap) => {
      if (snap.exists()) {
        banner1Data = snap.data();
      } else {
        banner1Data = null;
      }
      revalidateConfig();
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'config/banner1');
    });

    const unsubBanner2 = onSnapshot(doc(db, 'config', 'banner2'), { includeMetadataChanges: true }, (snap) => {
      if (snap.exists()) {
        banner2Data = snap.data();
      } else {
        banner2Data = null;
      }
      revalidateConfig();
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'config/banner2');
    });

    const unsubInlineAds = onSnapshot(doc(db, 'config', 'inlineAds'), { includeMetadataChanges: true }, (snap) => {
      if (snap.exists()) {
        inlineAdsData = snap.data();
      } else {
        inlineAdsData = null;
      }
      revalidateConfig();
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'config/inlineAds');
    });

    return () => {
      unsubAuth();
      unsubConfig();
      unsubBanner1();
      unsubBanner2();
      unsubInlineAds();
    };
  }, []);

  const handleLoginSuccess = useCallback(() => {
    setIsAdmin(true);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('adm_auth_session');
    setIsAdmin(false);
    auth.signOut().catch(err => console.error('Error signing out:', err));
  }, []);

  const contextValue = useMemo(() => ({
    isAdmin,
    config,
    handleLoginSuccess,
    handleLogout
  }), [isAdmin, config, handleLoginSuccess, handleLogout]);

  return (
    <AppContext.Provider value={contextValue}>
      <div className="relative min-h-screen">
        <PWAManager />
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
        
        {/* Welcome Toast Notification */}
        <WelcomeToast 
          show={toastData.show}
          userName={toastData.userName}
          userPhoto={toastData.userPhoto}
          onClose={() => setToastData(prev => ({ ...prev, show: false }))}
        />

        {/* PWA Enhancements */}
        <PWAInstallPrompt />
        <OfflineStatusNotifier />
        <PWAUpdateNotifier />
      </div>
    </AppContext.Provider>
  );
}

// Wrapper for Home to inject context props if they are expected directly (backward compatibility)
function HomeWrapper() {
  const { config } = useAppContext();
  return <Home config={config} />;
}

function AboutWrapper() {
  const { config } = useAppContext();
  return <About config={config} />;
}

function AdminWrapper() {
  const { isAdmin, config, handleLoginSuccess, handleLogout } = useAppContext();
  return isAdmin ? <AdminDashboard onLogout={handleLogout} /> : <Login config={config} onLoginSuccess={handleLoginSuccess} />;
}

function LoginWrapper() {
  const { config, handleLoginSuccess, isAdmin } = useAppContext();
  return isAdmin ? <Navigate to="/admin" replace /> : <Login config={config} onLoginSuccess={handleLoginSuccess} />;
}

// React Router v7 Data Router setup
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: "/",
        element: <HomeWrapper />
      },
      {
        path: "/about",
        element: <AboutWrapper />
      },
      {
        path: "/invoice/:id",
        element: <InvoicePage />
      },
      {
        path: "/admin",
        element: <AdminWrapper />
      },
      {
        path: "/admin/login",
        element: <LoginWrapper />
      }
    ]
  }
]);

export default function App() {
  return <RouterProvider router={router} />;
}
